import { useCallback, useMemo } from 'react';
import { useUserDocument } from '../contexts/UserDocumentContext';
import { 
  OpenPosition, 
  OpenOrder, 
  ClosedTrade, 
  TradingHistoryEntry,
  AccountSummary,
  AITradingData,
  ManualTradingData,
  TradingSettings,
  RiskManagement,
  PerformanceMetrics,
  FinancialTransaction
} from '../types/userDocument';

// Main hook for accessing user document
export const useUserDocumentData = () => {
  const context = useUserDocument();
  return context;
};

// Hook for account summary operations
export const useAccountSummary = (accountType: 'demo' | 'real' = 'demo') => {
  const { demoAccountData, realAccountData, updateAccountSummary, isLoading, error } = useUserDocument();

  const accountSummary = useMemo(() => {
    const accountData = accountType === 'demo' ? demoAccountData : realAccountData;
    return accountData?.accountSummary || null;
  }, [accountType, demoAccountData, realAccountData]);

  const updateSummary = useCallback(async (summary: Partial<AccountSummary>) => {
    await updateAccountSummary(accountType, summary);
  }, [accountType, updateAccountSummary]);

  return {
    accountSummary,
    updateSummary,
    isLoading,
    error
  };
};

// Hook for open positions
export const useOpenPositions = (accountType: 'demo' | 'real' = 'demo') => {
  const { demoAccountData, realAccountData, updateOpenPosition, removeOpenPosition, isLoading, error } = useUserDocument();

  const openPositions = useMemo(() => {
    const accountData = accountType === 'demo' ? demoAccountData : realAccountData;
    return accountData?.openPositions || [];
  }, [accountType, demoAccountData, realAccountData]);

  const getPositionById = useCallback((positionId: string) => {
    return openPositions.find(position => position.id === positionId);
  }, [openPositions]);

  const getPositionsBySymbol = useCallback((symbol: string) => {
    return openPositions.filter(position => position.symbol === symbol);
  }, [openPositions]);

  const getPositionsByType = useCallback((type: 'long' | 'short') => {
    return openPositions.filter(position => position.type === type);
  }, [openPositions]);

  const getPositionsBySource = useCallback((source: 'manual' | 'ai' | 'copy_trading') => {
    return openPositions.filter(position => position.source === source);
  }, [openPositions]);

  const totalUnrealizedPnL = useMemo(() => {
    return openPositions.reduce((total, position) => total + position.unrealizedPnL, 0);
  }, [openPositions]);

  const totalMarginUsed = useMemo(() => {
    return openPositions.reduce((total, position) => total + position.marginUsed, 0);
  }, [openPositions]);

  const updatePosition = useCallback(async (position: OpenPosition) => {
    await updateOpenPosition(accountType, position);
  }, [accountType, updateOpenPosition]);

  const removePosition = useCallback(async (positionId: string) => {
    await removeOpenPosition(accountType, positionId);
  }, [accountType, removeOpenPosition]);

  return {
    openPositions,
    getPositionById,
    getPositionsBySymbol,
    getPositionsByType,
    getPositionsBySource,
    totalUnrealizedPnL,
    totalMarginUsed,
    updatePosition,
    removePosition,
    isLoading,
    error
  };
};

// Hook for open orders
export const useOpenOrders = (accountType: 'demo' | 'real' = 'demo') => {
  const { demoAccountData, realAccountData, updateOpenOrder, removeOpenOrder, isLoading, error } = useUserDocument();

  const openOrders = useMemo(() => {
    const accountData = accountType === 'demo' ? demoAccountData : realAccountData;
    return accountData?.openOrders || [];
  }, [accountType, demoAccountData, realAccountData]);

  const getOrderById = useCallback((orderId: string) => {
    return openOrders.find((order: any) => order.id === orderId);
  }, [openOrders]);

  const getOrdersBySymbol = useCallback((symbol: string) => {
    return openOrders.filter((order: any) => order.symbol === symbol);
  }, [openOrders]);

  const getOrdersByType = useCallback((type: string) => {
    return openOrders.filter((order: any) => order.type === type);
  }, [openOrders]);

  const getOrdersByStatus = useCallback((status: string) => {
    return openOrders.filter((order: any) => order.status === status);
  }, [openOrders]);

  const getOrdersBySource = useCallback((source: 'manual' | 'ai' | 'copy_trading') => {
    return openOrders.filter((order: any) => order.source === source);
  }, [openOrders]);

  const pendingOrders = useMemo(() => {
    return openOrders.filter((order: any) => order.status === 'pending');
  }, [openOrders]);

  const updateOrder = useCallback(async (order: OpenOrder) => {
    await updateOpenOrder(accountType, order);
  }, [accountType, updateOpenOrder]);

  const removeOrder = useCallback(async (orderId: string) => {
    await removeOpenOrder(accountType, orderId);
  }, [accountType, removeOpenOrder]);

  return {
    openOrders,
    getOrderById,
    getOrdersBySymbol,
    getOrdersByType,
    getOrdersByStatus,
    getOrdersBySource,
    pendingOrders,
    updateOrder,
    removeOrder,
    isLoading,
    error
  };
};

// Hook for closed trades
export const useClosedTrades = (accountType: 'demo' | 'real' = 'demo') => {
  const { demoAccountData, realAccountData, addClosedTrade, isLoading, error } = useUserDocument();

  const closedTrades = useMemo(() => {
    const accountData = accountType === 'demo' ? demoAccountData : realAccountData;
    return accountData?.closedTrades || [];
  }, [accountType, demoAccountData, realAccountData]);

  const getTradeById = useCallback((tradeId: string) => {
    return closedTrades.find(trade => trade.id === tradeId);
  }, [closedTrades]);

  const getTradesBySymbol = useCallback((symbol: string) => {
    return closedTrades.filter(trade => trade.symbol === symbol);
  }, [closedTrades]);

  const getTradesByType = useCallback((type: 'long' | 'short') => {
    return closedTrades.filter(trade => trade.type === type);
  }, [closedTrades]);

  const getTradesBySource = useCallback((source: 'manual' | 'ai' | 'copy_trading') => {
    return closedTrades.filter(trade => trade.source === source);
  }, [closedTrades]);

  const getTradesByDateRange = useCallback((startDate: Date, endDate: Date) => {
    return closedTrades.filter(trade => {
      // Ensure closedAt is a Date object
      const closedAtDate = trade.closedAt instanceof Date ? trade.closedAt : new Date(trade.closedAt);
      return closedAtDate >= startDate && closedAtDate <= endDate;
    });
  }, [closedTrades]);

  const totalRealizedPnL = useMemo(() => {
    return closedTrades.reduce((total, trade) => total + trade.realizedPnL, 0);
  }, [closedTrades]);

  const winningTrades = useMemo(() => {
    return closedTrades.filter(trade => trade.realizedPnL > 0);
  }, [closedTrades]);

  const losingTrades = useMemo(() => {
    return closedTrades.filter(trade => trade.realizedPnL < 0);
  }, [closedTrades]);

  const winRate = useMemo(() => {
    if (closedTrades.length === 0) return 0;
    return (winningTrades.length / closedTrades.length) * 100;
  }, [closedTrades, winningTrades]);

  const averageWin = useMemo(() => {
    if (winningTrades.length === 0) return 0;
    return winningTrades.reduce((total, trade) => total + trade.realizedPnL, 0) / winningTrades.length;
  }, [winningTrades]);

  const averageLoss = useMemo(() => {
    if (losingTrades.length === 0) return 0;
    return losingTrades.reduce((total, trade) => total + trade.realizedPnL, 0) / losingTrades.length;
  }, [losingTrades]);

  const addTrade = useCallback(async (trade: ClosedTrade) => {
    await addClosedTrade(accountType, trade);
  }, [accountType, addClosedTrade]);

  return {
    closedTrades,
    getTradeById,
    getTradesBySymbol,
    getTradesByType,
    getTradesBySource,
    getTradesByDateRange,
    totalRealizedPnL,
    winningTrades,
    losingTrades,
    winRate,
    averageWin,
    averageLoss,
    addTrade,
    isLoading,
    error
  };
};

// Hook for trading history
export const useTradingHistory = (accountType: 'demo' | 'real' = 'demo') => {
  const { demoAccountData, realAccountData, addTradingHistoryEntry, isLoading, error } = useUserDocument();

  const tradingHistory = useMemo(() => {
    const accountData = accountType === 'demo' ? demoAccountData : realAccountData;
    return accountData?.tradingHistory || [];
  }, [accountType, demoAccountData, realAccountData]);

  const getHistoryByType = useCallback((type: string) => {
    return tradingHistory.filter((entry: any) => entry.type === type);
  }, [tradingHistory]);

  const getHistoryByDateRange = useCallback((startDate: Date, endDate: Date) => {
    return tradingHistory.filter((entry: any) => 
      entry.timestamp >= startDate && entry.timestamp <= endDate
    );
  }, [tradingHistory]);

  const getHistoryBySource = useCallback((source: string) => {
    return tradingHistory.filter((entry: any) => entry.source === source);
  }, [tradingHistory]);

  const recentHistory = useMemo(() => {
    return tradingHistory
      .sort((a: any, b: any) => b.timestamp.getTime() - a.timestamp.getTime())
      .slice(0, 50); // Last 50 entries
  }, [tradingHistory]);

  const addHistoryEntry = useCallback(async (entry: TradingHistoryEntry) => {
    await addTradingHistoryEntry(accountType, entry);
  }, [accountType, addTradingHistoryEntry]);

  return {
    tradingHistory,
    getHistoryByType,
    getHistoryByDateRange,
    getHistoryBySource,
    recentHistory,
    addHistoryEntry,
    isLoading,
    error
  };
};

// Hook for AI trading data
export const useAITrading = (accountType: 'demo' | 'real' = 'demo') => {
  const { demoAccountData, realAccountData, updateAITradingData, isLoading, error } = useUserDocument();

  const aiTradingData = useMemo(() => {
    const accountData = accountType === 'demo' ? demoAccountData : realAccountData;
    return accountData?.aiTradingData || null;
  }, [accountType, demoAccountData, realAccountData]);

  const isAIEnabled = useMemo(() => {
    return aiTradingData?.enabled || false;
  }, [aiTradingData]);

  const activeStrategies = useMemo(() => {
    return aiTradingData?.activeStrategies || [];
  }, [aiTradingData]);

  const strategies = useMemo(() => {
    return aiTradingData?.strategies || [];
  }, [aiTradingData]);

  const getStrategyById = useCallback((strategyId: string) => {
    return strategies.find((strategy: any) => strategy.id === strategyId);
  }, [strategies]);

  const updateAI = useCallback(async (aiData: Partial<AITradingData>) => {
    await updateAITradingData(accountType, aiData);
  }, [accountType, updateAITradingData]);

  return {
    aiTradingData,
    isAIEnabled,
    activeStrategies,
    strategies,
    getStrategyById,
    updateAI,
    isLoading,
    error
  };
};

// Hook for manual trading data
export const useManualTrading = (accountType: 'demo' | 'real' = 'demo') => {
  const { demoAccountData, realAccountData, updateManualTradingData, isLoading, error } = useUserDocument();

  const manualTradingData = useMemo(() => {
    const accountData = accountType === 'demo' ? demoAccountData : realAccountData;
    return accountData?.manualTradingData || null;
  }, [accountType, demoAccountData, realAccountData]);

  const preferences = useMemo(() => {
    return manualTradingData?.preferences || null;
  }, [manualTradingData]);

  const shortcuts = useMemo(() => {
    return manualTradingData?.shortcuts || null;
  }, [manualTradingData]);

  const updateManual = useCallback(async (manualData: Partial<ManualTradingData>) => {
    await updateManualTradingData(accountType, manualData);
  }, [accountType, updateManualTradingData]);

  return {
    manualTradingData,
    preferences,
    shortcuts,
    updateManual,
    isLoading,
    error
  };
};

// Hook for trading settings
export const useTradingSettings = (accountType: 'demo' | 'real' = 'demo') => {
  const { demoAccountData, realAccountData, updateTradingSettings, isLoading, error } = useUserDocument();

  const tradingSettings = useMemo(() => {
    const accountData = accountType === 'demo' ? demoAccountData : realAccountData;
    return accountData?.tradingSettings || null;
  }, [accountType, demoAccountData, realAccountData]);

  const generalSettings = useMemo(() => {
    return tradingSettings?.general || null;
  }, [tradingSettings]);

  const notificationSettings = useMemo(() => {
    return tradingSettings?.notifications || null;
  }, [tradingSettings]);

  const displaySettings = useMemo(() => {
    return tradingSettings?.display || null;
  }, [tradingSettings]);

  const riskSettings = useMemo(() => {
    return tradingSettings?.risk || null;
  }, [tradingSettings]);

  const updateSettings = useCallback(async (settings: Partial<TradingSettings>) => {
    await updateTradingSettings(accountType, settings);
  }, [accountType, updateTradingSettings]);

  return {
    tradingSettings,
    generalSettings,
    notificationSettings,
    displaySettings,
    riskSettings,
    updateSettings,
    isLoading,
    error
  };
};

// Hook for risk management
export const useRiskManagement = (accountType: 'demo' | 'real' = 'demo') => {
  const { demoAccountData, realAccountData, updateRiskManagement, isLoading, error } = useUserDocument();

  const riskManagement = useMemo(() => {
    const accountData = accountType === 'demo' ? demoAccountData : realAccountData;
    return accountData?.riskManagement || null;
  }, [accountType, demoAccountData, realAccountData]);

  const isRiskManagementEnabled = useMemo(() => {
    return riskManagement?.enabled || false;
  }, [riskManagement]);

  const updateRisk = useCallback(async (riskData: Partial<RiskManagement>) => {
    await updateRiskManagement(accountType, riskData);
  }, [accountType, updateRiskManagement]);

  return {
    riskManagement,
    isRiskManagementEnabled,
    updateRisk,
    isLoading,
    error
  };
};

// Hook for performance metrics
export const usePerformanceMetrics = (accountType: 'demo' | 'real' = 'demo') => {
  const { demoAccountData, realAccountData, updatePerformanceMetrics, isLoading, error } = useUserDocument();

  const performanceMetrics = useMemo(() => {
    const accountData = accountType === 'demo' ? demoAccountData : realAccountData;
    return accountData?.performanceMetrics || null;
  }, [accountType, demoAccountData, realAccountData]);

  const overallMetrics = useMemo(() => {
    return performanceMetrics?.overall || null;
  }, [performanceMetrics]);

  const monthlyMetrics = useMemo(() => {
    return performanceMetrics?.monthly || [];
  }, [performanceMetrics]);

  const yearlyMetrics = useMemo(() => {
    return performanceMetrics?.yearly || [];
  }, [performanceMetrics]);

  const updateMetrics = useCallback(async (metrics: Partial<PerformanceMetrics>) => {
    await updatePerformanceMetrics(accountType, metrics);
  }, [accountType, updatePerformanceMetrics]);

  return {
    performanceMetrics,
    overallMetrics,
    monthlyMetrics,
    yearlyMetrics,
    updateMetrics,
    isLoading,
    error
  };
};

// Hook for financial transactions
export const useFinancialTransactions = (accountType: 'demo' | 'real' = 'demo') => {
  const { demoAccountData, realAccountData, addFinancialTransaction, isLoading, error } = useUserDocument();

  const financialTransactions = useMemo(() => {
    const accountData = accountType === 'demo' ? demoAccountData : realAccountData;
    return accountData?.financialTransactions || [];
  }, [accountType, demoAccountData, realAccountData]);

  const getTransactionById = useCallback((transactionId: string) => {
    return financialTransactions.find((transaction: any) => transaction.id === transactionId);
  }, [financialTransactions]);

  const getTransactionsByType = useCallback((type: string) => {
    return financialTransactions.filter((transaction: any) => transaction.type === type);
  }, [financialTransactions]);

  const getTransactionsByStatus = useCallback((status: string) => {
    return financialTransactions.filter((transaction: any) => transaction.status === status);
  }, [financialTransactions]);

  const getTransactionsByDateRange = useCallback((startDate: Date, endDate: Date) => {
    return financialTransactions.filter((transaction: any) => 
      transaction.createdAt >= startDate && transaction.createdAt <= endDate
    );
  }, [financialTransactions]);

  const deposits = useMemo(() => {
    return getTransactionsByType('deposit');
  }, [getTransactionsByType]);

  const withdrawals = useMemo(() => {
    return getTransactionsByType('withdrawal');
  }, [getTransactionsByType]);

  const totalDeposits = useMemo(() => {
    return deposits.reduce((total: any, deposit: any) => total + deposit.amount, 0);
  }, [deposits]);

  const totalWithdrawals = useMemo(() => {
    return withdrawals.reduce((total: any, withdrawal: any) => total + withdrawal.amount, 0);
  }, [withdrawals]);

  const addTransaction = useCallback(async (transaction: FinancialTransaction) => {
    await addFinancialTransaction(accountType, transaction);
  }, [accountType, addFinancialTransaction]);

  return {
    financialTransactions,
    getTransactionById,
    getTransactionsByType,
    getTransactionsByStatus,
    getTransactionsByDateRange,
    deposits,
    withdrawals,
    totalDeposits,
    totalWithdrawals,
    addTransaction,
    isLoading,
    error
  };
};

// Hook for trading panel connections
export const useTradingPanelConnections = () => {
  const { userDocument, updateUserDocument, isLoading, error } = useUserDocument();

  const tradingPanelConnections = useMemo(() => {
    return userDocument?.tradingPanelConnections || [];
  }, [userDocument]);

  const getConnectionById = useCallback((connectionId: string) => {
    return tradingPanelConnections.find(connection => connection.id === connectionId);
  }, [tradingPanelConnections]);

  const getConnectionsByType = useCallback((type: string) => {
    return tradingPanelConnections.filter(connection => connection.type === type);
  }, [tradingPanelConnections]);

  const getConnectionsByStatus = useCallback((status: string) => {
    return tradingPanelConnections.filter(connection => connection.status === status);
  }, [tradingPanelConnections]);

  const activeConnections = useMemo(() => {
    return getConnectionsByStatus('connected');
  }, [getConnectionsByStatus]);

  const updateConnections = useCallback(async (connections: any[]) => {
    await updateUserDocument({ tradingPanelConnections: connections });
  }, [updateUserDocument]);

  return {
    tradingPanelConnections,
    getConnectionById,
    getConnectionsByType,
    getConnectionsByStatus,
    activeConnections,
    updateConnections,
    isLoading,
    error
  };
};
